// E12 — leave balances read in DAYS, the unit Turkish annual leave is discussed
// in; the stored/edited unit stays hours (`_hours`, never money), so the hours
// figure survives as a quieter second line whenever it says something the days
// do not (a whole day or more).
//
// The day length is the SDK constant the server derives every request's draw
// from (`HOURS_PER_WORKING_DAY`, time-attendance-sdk/leave-duration.ts). No
// per-policy or per-tenant setting exists to read instead — that file explains
// why inventing one would be a second source of truth — so the conversion here
// and the draw there can never disagree.

import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { HOURS_PER_WORKING_DAY } from "@novemai/time-attendance-sdk";

import { formatHours } from "@/lib/hours";

export interface LeaveSpan {
  /** Whole working days (always >= 0; the sign lives on `negative`). */
  readonly days: number;
  /** What is left of the last day, in hours (0 <= hours < one working day). */
  readonly hours: number;
  readonly negative: boolean;
}

/** Hours → whole working days + leftover hours; null when the input is not a number. */
export function splitLeaveHours(value: string | number): LeaveSpan | null {
  const raw = typeof value === "string" ? value.trim() : value;
  if (raw === "") return null;
  const numeric = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(numeric)) return null;

  const magnitude = Math.abs(numeric);
  // The epsilon absorbs float drift (e.g. 23.999999999 from summed decimals) so
  // a whole number of days never renders as "2 gün 7,99 saat".
  const days = Math.floor((magnitude + 1e-9) / HOURS_PER_WORKING_DAY);
  const hours = Math.round((magnitude - days * HOURS_PER_WORKING_DAY) * 100) / 100;
  return { days, hours, negative: numeric < 0 && (days > 0 || hours > 0) };
}

/** "21 gün", "2 gün 4 saat" or "4 saat"; null when `value` is not a number. */
export function formatLeaveSpan(
  value: string | number,
  locale: string,
  t: TFunction,
): string | null {
  const span = splitLeaveHours(value);
  if (span === null) return null;

  const days = new Intl.NumberFormat(locale).format(span.days);
  const hours = formatHours(span.hours, locale) ?? "";
  const text =
    span.days === 0
      ? t("leave.balance.hours_value", { hours })
      : span.hours === 0
        ? t("leave.balance.days_value", { days })
        : t("leave.balance.days_hours_value", { days, hours });
  return span.negative ? `-${text}` : text;
}

/**
 * The balance figure as the screens draw it: days first, then the same amount
 * in hours as a muted second line — only when it is at least a day, since
 * "4 saat (4 saat)" would be noise.
 */
export function LeaveSpanText({
  hours,
  className,
}: {
  readonly hours: string | number;
  readonly className?: string;
}): React.ReactElement {
  const { t, i18n } = useTranslation();
  const primary = formatLeaveSpan(hours, i18n.language, t) ?? t("common.unknown");
  const span = splitLeaveHours(hours);
  const asHours = formatHours(hours, i18n.language);
  const showHours = span !== null && span.days > 0 && asHours !== null;
  return (
    <>
      <span className={className}>{primary}</span>
      {showHours && (
        <span className="ml-1.5 font-sans text-[11.5px] font-normal text-[var(--fg-muted)]">
          {t("leave.balance.hours_in_parens", {
            hours: t("leave.balance.hours_value", { hours: asHours }),
          })}
        </span>
      )}
    </>
  );
}
